import React, { useState } from 'react';
import '../CSS/JobUpload.css';

const UploadMonthlyNews: React.FC = () => {
  const [formData, setFormData] = useState({
    monthlyNewsTitle: '',
    monthlyNewsDescription: '',
    newsArticleFile: null as File | null
  });

  const [descCharCount, setDescCharCount] = useState(0);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const MAX_DESC = 5000;

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'monthlyNewsDescription') {
      setDescCharCount(value.length);
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData(prev => ({
      ...prev,
      newsArticleFile: file
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.monthlyNewsTitle.trim()) {
      alert('Title is required');
      return false;
    }
    if (!formData.monthlyNewsDescription.trim()) {
      alert('Description is required');
      return false;
    }
    if (descCharCount > MAX_DESC) {
      alert(`Description exceeds the maximum character limit of ${MAX_DESC}`);
      return false;
    }
    if (!formData.newsArticleFile) {
      alert('News Article Upload is required');
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      setShowConfirmation(false);
      return;
    }

    setIsSubmitting(true);
    try {
      // Get token from localStorage or sessionStorage
      const token = localStorage.getItem('adminToken') || sessionStorage.getItem('adminToken');
      
      // Create FormData for file upload
      const submitData = new FormData();
      submitData.append('monthlyNewsTitle', formData.monthlyNewsTitle.trim());
      submitData.append('monthlyNewsDescription', formData.monthlyNewsDescription.trim());
      if (formData.newsArticleFile) {
        submitData.append('newsArticleFile', formData.newsArticleFile);
      }
      
      const response = await fetch('http://127.0.0.1:3060/api/admin/monthly-news', {
        method: 'POST',
        headers: {
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: submitData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to create monthly news');
      }

      alert('Monthly news uploaded successfully!');
      
      // Reset form
      setFormData({
        monthlyNewsTitle: '',
        monthlyNewsDescription: '',
        newsArticleFile: null
      });
      setDescCharCount(0);
      setShowConfirmation(false);
      // Reset file input
      const fileInput = document.getElementById('newsArticleFile') as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
    } catch (error: any) {
      console.error('Error submitting form:', error);
      alert(error.message || 'Error submitting form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      setShowConfirmation(true);
    }
  };

  return (
    <div className="job-upload-page">
      <div className="job-upload-container">
        <h1 className="job-upload-title">Upload Monthly News</h1>
        
        <form className="job-upload-form" onSubmit={handleFormSubmit}>
          <div className="job-upload-field">
            <label htmlFor="monthlyNewsTitle" className="job-upload-label">
              Title <span className="required">*</span>
            </label>
            <input
              type="text"
              id="monthlyNewsTitle"
              name="monthlyNewsTitle"
              value={formData.monthlyNewsTitle}
              onChange={handleInputChange}
              className="job-upload-input"
              required
            />
          </div>

          <div className="job-upload-field">
            <label htmlFor="monthlyNewsDescription" className="job-upload-label">
              Description <span className="required">*</span>
              <span className="char-count">({descCharCount} / {MAX_DESC} characters)</span>
            </label>
            <textarea
              id="monthlyNewsDescription"
              name="monthlyNewsDescription"
              value={formData.monthlyNewsDescription}
              onChange={handleInputChange}
              className="job-upload-textarea"
              rows={8}
              maxLength={MAX_DESC}
              required
            />
            {descCharCount > MAX_DESC && (
              <div className="char-count-error">
                Character limit exceeded. Please reduce the content.
              </div>
            )}
          </div>

          <div className="job-upload-field">
            <label htmlFor="newsArticleFile" className="job-upload-label">
              News Article Upload <span className="required">*</span>
            </label>
            <input
              type="file"
              id="newsArticleFile"
              name="newsArticleFile"
              onChange={handleFileChange}
              className="job-upload-file-input"
              accept=".pdf,.doc,.docx"
              required
            />
            {formData.newsArticleFile && (
              <div className="file-preview">
                <span className="file-name">{formData.newsArticleFile.name}</span>
              </div>
            )}
          </div>

          <button type="submit" className="job-upload-submit-button">
            Submit
          </button>
        </form>
      </div>

      {/* Confirmation Overlay */}
      {showConfirmation && (
        <div className="confirmation-overlay">
          <div className="confirmation-modal">
            <h2 className="confirmation-title">Confirm Submission</h2>
            <p className="confirmation-message">
              Are you sure that all information filled out is correct before continuing with the submission?
            </p>
            <div className="confirmation-buttons">
              <button
                type="button"
                onClick={handleSubmit}
                className="confirmation-btn confirmation-btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Yes, Submit'}
              </button>
              <button
                type="button"
                onClick={() => setShowConfirmation(false)}
                className="confirmation-btn confirmation-btn-secondary"
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UploadMonthlyNews;

